/**
 * @todo temporary stop for import. Implement seperationf from hsbshare first.
 */
import Issue, { Priorities, States } from '../Data/Issue';
import { Viewer } from '../Viewer';
import { PointerButtons } from '../Events/ViewerEvents';
import { ViewerLayers, ViewerLayersBit } from '../Enums/ViewerLayers';
import { ViewerScene } from '../ViewerScene';
import { Picker } from '../Tools/Picker';
import { ViewerTool } from '../Tools/ViewerBaseTool';
import * as THREE from 'three';
import { SceneLayerName } from '../../hsbcad-model';
import { getPropertyDescriptor } from '../Helpers/ObjectHelpers';
import { CameraView, IfcReference } from '../../isharedata';
import { EntityHandleReference } from '../../isharedata';

/**
 * Tool used to place, moved and open issues.
 */
export class IssueManager {
    /**
     * Map containing references to issue sprites (key = creation date)
     */
    private _issues: Map<string, THREE.Sprite> = new Map();
    /**
     * Currently hovered over issue object
     */
    private _highLightedIssue: THREE.Sprite | null = null;
    private set highLightedIssue(sprite: THREE.Sprite | null) {
        if (this._highLightedIssue) {
            this._highLightedIssue.scale.set(this.spriteSize, this.spriteSize, 1);
            this._highLightedIssue.material.opacity = 0.5;
        }
        this._highLightedIssue = sprite;
        if (this._highLightedIssue) {
            this._highLightedIssue.scale.set(this.spriteSize * 2, this.spriteSize * 2, 1);
            this._highLightedIssue.material.opacity = 1;
        }
        this._viewer.requestRenderFrame();
    }
    /**
     * Currently hovered over visual (when dragging an issue object)
     */
    private _highlighted: THREE.Object3D | null = null;
    /**
     * Selected issue object
     */
    private _clickedIssueObj: THREE.Sprite | null = null;
    private set clickedIssueObj(sprite: THREE.Sprite) {
        if (sprite == this._clickedIssueObj) return;

        if (this._clickedIssueObj) {
            this.setFillTransparent(this._clickedIssueObj.material, false);
        }
        this._clickedIssueObj = sprite;
        if (this._clickedIssueObj) {
            this.setFillTransparent(this._clickedIssueObj.material, true);
        }
    }
    /**
     * Start positions for drag
     * @member {THREE.Vector3}: Original world position of the issue object.
     * @member {THREE.Vector2}: Original screen pos on start of drag.
     */
    private _startDrag: [THREE.Vector3, THREE.Vector2] | null = null;
    /**
     * Treshold for dragging. If drag is shorter will regard the drag as only a click. IF longer will actually consider it a drag.
     */
    private _dragTreshold = 0.05;

    /**
     * The scaling size for issues
     */
    private _spriteSize: number = 200;
    /**
     * The scaling size for issues
     */
    public get spriteSize(): number {
        return this._spriteSize;
    }
    /**
     * The scaling size for issues
     */
    public set spriteSize(v: number) {
        this._spriteSize = v;
    }

    constructor(private _viewer: Viewer) {}

    addIssue(issue: Issue) {
        let sprite: THREE.Sprite | undefined;
        if (this._issues.has(issue.id)) {
            sprite = this._issues.get(issue.id);
        } else {
            sprite = this.CreateIssueObject(issue);
            this._issues.set(issue.id, sprite);
        }
        if (!sprite) return;
        sprite.userData.issue = issue;
        let currSelection = this._viewer.currentSelection;
        let center = currSelection
            .map((e) => e.coordSys.ptOrg)
            .reduce((accu, curr) => (accu ? accu.add(curr) : curr))
            .divideScalar(currSelection.length);
        sprite.position.copy(center);
        this.SetIssueColor(sprite.material, issue.state);
        this._viewer.currentScene?.GetLayer(SceneLayerName.Label).attach(sprite);
        this._viewer.ViewerEvents?.ToolEvents.emit('issue-added', {
            issue,
            sprite,
        });
        return sprite;
    }

    loadIssuesNodes(issueNodes: any) {
        let issues = issueNodes.map((node: { metadata: { data: any } }) => {
            let metadata = node.metadata.data;
            let issue = new Issue(metadata.properties.title.value) as any;
            Object.entries(metadata.properties).forEach(([key, value]: [string, any]) => {
                let descriptor = getPropertyDescriptor(issue, key);
                if (descriptor?.set) {
                    issue[key] = value?.value ?? value ?? '';
                }
            });
            issue.references = metadata.data?.modelReferences;
            issue._id = metadata._id;
            return issue;
        });
        this.loadIssues(issues);
    }

    /**
     * Loads all issues and places them in the scene.
     * @param data
     */
    loadIssues(issues: Issue[]) {
        this._issues.clear();
        let entityTable = this._viewer.project?.EntityTable;
        if (!entityTable) return;
        // We collect all world positions for the reference entities
        issues
            .filter((i) => i.references && Object.keys(i.references).length > 0)
            .forEach((issue) => {
                let sprite = this.CreateIssueObject(issue);
                this._issues.set(issue.id, sprite);

                let positions: THREE.Vector3[] = [];
                Object.entries(issue.references).forEach(([modelId, refs]) => {
                    let entities = entityTable![modelId];
                    // No model found
                    if (!entities) return;
                    refs.forEach((ref) => {
                        let entity = (ref as EntityHandleReference)
                            ? entities[(ref as EntityHandleReference).handle]
                            : Object.values(entities).find(
                                  (e) => e.ifcid === (ref as IfcReference).ifcid
                              );
                        // If no entity found
                        if (!entity) return;
                        let obj = entity.object;
                        // If instanced mesh we need instance matrix
                        if (obj instanceof THREE.InstancedMesh) {
                            let pos = obj.getWorldPosition(new THREE.Vector3());
                            let m4 = new THREE.Matrix4();
                            obj.getMatrixAt(entity.instanceId, m4);
                            pos.applyMatrix4(m4);
                            positions.push(pos);
                            return pos;
                        } else if (obj instanceof THREE.Mesh) {
                            // Else we jsut take object world pos
                            positions.push(obj.getWorldPosition(new THREE.Vector3()));
                        }
                        // Else if no object we ignore
                        else return;
                    });
                });
                if (positions.length > 0) {
                    let centerPos = positions
                        .reduce((a, b) => a!.add(b!))
                        ?.divideScalar(positions.length);
                    sprite.position.copy(centerPos);
                    this._viewer.currentScene?.add(sprite);
                }
            });
    }

    /**
     * Updates the sprite based on the given issue shareData
     * @param sharedata
     */
    IssueUpdate(issueId: string) {
        let sprite = this._issues.get(issueId);
        if (sprite) {
            this.SetIssueColor(sprite.material, sprite.userData.issue.state);
        }
    }

    /**
     * Deletes the sprite object linked to the given issue data
     * @param sharedata
     */
    IssueDeleted(issueId: string) {
        let sprite = this._issues.get(issueId);
        if (sprite) {
            this._viewer.SceneManager.RemoveFromActiveScene(sprite);
            this._issues.delete(issueId);
        }
    }

    /**
     * Emits event for the selected issue (should open dialog)
     * @param sprite
     */
    ShowIssue(sprite: THREE.Object3D) {
        let issue = sprite.userData.issue;
        this._viewer.ViewerEvents?.ToolEvents.emit('issueSelected', {
            issue,
            sprite,
        });
    }

    /**
     * Sets the camera to the correct transform for the given data
     * @param shareData Should contain a cameraview
     * @returns
     */
    SetCamera(shareData: { cameraView: CameraView }) {
        if (!shareData.cameraView?.view) return;
        this._viewer.cameraUtils.setCameraView(shareData.cameraView);
    }

    /**
     * Enlarges the given issue object to highlight it
     * @param issueObject
     */
    highlightIssueSprite(issueObject: THREE.Sprite) {
        if (issueObject === this._highLightedIssue) return;
        this.highLightedIssue = issueObject;
    }

    /**
     * Enlarges the given issue object to highlight it
     * @param issueObject
     */
    highlightIssue(issueId: string | null) {
        if (!issueId) this.highLightedIssue = null;
        else {
            let sprite = this._issues.get(issueId);
            if (sprite) this.highlightIssueSprite(sprite);
        }
    }

    /**
     * Creates a default issue object
     * @returns {THREE.Sprite} default issue object
     */
    CreateIssueObject(issue: any): THREE.Sprite {
        var canvas = this.CreateSprite(issue);

        var texture = new THREE.Texture(canvas);
        texture.minFilter = THREE.LinearFilter;
        texture.needsUpdate = true;
        var spriteMat = new THREE.SpriteMaterial({
            map: texture,
            sizeAttenuation: true,
            depthTest: false,
        });
        var sprite = new THREE.Sprite(spriteMat);
        sprite.scale.set(this.spriteSize, this.spriteSize, 1);
        sprite.layers.set(ViewerLayers.ISSUES);
        sprite.userData.issue = issue;
        sprite.userData.visible = true;
        return sprite;
    }

    /**
     * Creates sprite for an issue object
     * @returns
     */
    CreateSprite(issue: any, fill: boolean = true, stroke: boolean = true) {
        var radius = 200;
        var lineWidth = radius / 4;
        var canvas = document.createElement('canvas');
        canvas.width = radius * 2;
        canvas.height = radius * 2;
        var ctx = canvas.getContext('2d');
        canvas.style.pointerEvents = 'auto';
        if (!ctx) return;

        ctx.lineWidth = lineWidth;
        ctx.fillStyle = '#12ba12';
        ctx.strokeStyle = '#fff';

        ctx.beginPath();
        ctx.arc(radius, radius, radius - lineWidth * 0.5, 0, 2 * Math.PI);
        ctx.closePath();
        if (stroke) ctx.stroke();
        if (fill) ctx.fill();

        return canvas;
    }

    /**
     * Sets the color of a sprite material based on the given urgency
     * @param material
     * @param urgency
     */
    SetIssueColor(material: THREE.SpriteMaterial, state: States | null = States.ACTIVE) {
        switch (state) {
            case States.OPEN:
                material.color.set('#E6F6CF');
                break;
            case States.ACTIVE:
                material.color.set('#E0F1FB');
                break;
            case States.IN_PROGRESS:
                material.color.set('#A6E0FC');
                break;
            case States.ON_HOLD:
                material.color.set('#E6F6CF');
                break;
            case States.RESOLVED:
                material.color.set('#0070E4');
                break;
            case States.CLOSED:
                material.color.set('#FFFFFF');
                break;
            case States.DUE:
                material.color.set('#E30000');
                break;
            default:
                material.color.set('#E6F6CF');
                break;
        }
        material.needsUpdate = true;
    }

    setFillTransparent(material: THREE.SpriteMaterial, transparent: boolean) {
        var canvas = this.CreateSprite(!transparent);
        var texture = new THREE.Texture(canvas);
        texture.minFilter = THREE.LinearFilter;
        texture.needsUpdate = true;
        material.map = texture;
        material.needsUpdate = true;
    }
}
